/**
 * Tauri StorageAdapter implementation.
 *
 * Replaces the IndexedDB persistence layer with filesystem-backed
 * `.orbital` files via Tauri commands defined in `tauri/src/lib.rs`.
 *
 * Network sync is OFF by default in Tauri (local-first). It will be
 * turned on per-document by the Phase 5 share flow; for Phase 2 the
 * adapter returns null from `getSyncUrl` and `false` from
 * `isSyncAvailable` so SyncManager / UserSyncManager skip their
 * connect path entirely.
 *
 * Per-doc UI prefs (theme, last-opened, transforms, vault sizes) stay
 * in webview localStorage — Tauri's webview supports it natively and
 * these are device-local anyway. A future phase may migrate them to a
 * config file without touching call sites.
 *
 * See docs/architecture/storage-adapter.md for the contract.
 */

import * as Y from "yjs";
import { assertCompatibleVaultStructure } from "../sync/vault-structure.js";
import { t } from "../../shared/i18n/index.js";

// Re-export the localStorage-backed pref helpers from the web adapter.
// Tauri's webview supports localStorage and these prefs are device-local;
// no need to round-trip through IPC.
export {
  getPref,
  setPref,
  removePref,
  clearPrefsByPrefix,
} from "./web-adapter.js";
import { notify } from "../host.js";
import { NOTICE } from "../notices.js";

// ============================================
// Tauri invoke shim
// ============================================
//
// We call `window.__TAURI_INTERNALS__.invoke` directly rather than
// importing `@tauri-apps/api/core`. This lets us add the Tauri adapter
// without adding the npm package as a frontend dependency — the web
// build never references this code at runtime (the runtime probe in
// `index.js` selects the web adapter when __TAURI_INTERNALS__ is
// absent), and bundlers tree-shake the unused exports.

function getInvoke() {
  if (typeof window === "undefined" || !window.__TAURI_INTERNALS__) {
    throw new Error("Tauri runtime not detected — invoke unavailable");
  }
  return window.__TAURI_INTERNALS__.invoke;
}

// Phase 5a: server URL is dynamic. The adapter starts in local-only
// mode (URL = null) and `loadAdapterConfig()` may set a real URL at
// startup if the user has opted in via the tauri config.json. While
// the URL is null, getSyncUrl returns null and SyncManager /
// UserSyncManager skip their connect path — same behavior as before.
let _serverUrl = null;
let _configLoaded = false;

const WRITE_DEBOUNCE_MS = 500;

// Map and cache files cross the bridge raw, as attachments do (tauri/src/
// lib.rs): as JSON arrays of numbers a 10 MB map was about 36 MB of JSON
// each way at every save (0.24 s to build it, measured in Chromium). A read
// answers no bytes when there is no file.
function fileBytes(result) {
  if (result instanceof ArrayBuffer) return new Uint8Array(result);
  if (ArrayBuffer.isView(result)) return new Uint8Array(result.buffer, result.byteOffset, result.byteLength);
  return new Uint8Array(result || []);
}

/** A vault file, raw: "main" (<vault>/<id>.orbital) or "cache"
 * (.orbital-cache/). No bytes: no file. */
export async function readVaultFile(kind, name) {
  const result = kind === "main"
    ? await getInvoke()("doc_read", { docId: name })
    : await getInvoke()("cache_read", { name });
  return fileBytes(result);
}

export async function writeVaultFile(kind, name, bytes) {
  await getInvoke()(kind === "main" ? "doc_write" : "cache_write", bytes, {
    headers: kind === "main" ? { "x-doc-id": name } : { "x-name": name },
  });
}

// Four bytes at a time when both are aligned (a 10 MB file is compared at
// every save).
function sameBytes(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  let i = 0;
  if (a.byteOffset % 4 === 0 && b.byteOffset % 4 === 0) {
    const words = a.length >>> 2;
    const wa = new Uint32Array(a.buffer, a.byteOffset, words);
    const wb = new Uint32Array(b.buffer, b.byteOffset, words);
    for (; i < words; i++) if (wa[i] !== wb[i]) return false;
    i = words * 4;
  }
  for (; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// Keep failed writes registered so close/reload can retry them.
const _liveInstances = new Set();
// Providers may overlap during sharing/recovery. Serialize each physical file's
// read/merge/write, not just each provider's writes.
const _fileOperations = new Map();
function withFileLock(key, action) {
  const previous = _fileOperations.get(key) || Promise.resolve();
  const operation = previous.then(action);
  const tail = operation.catch(() => {});
  _fileOperations.set(key, tail);
  void tail.then(() => {
    if (_fileOperations.get(key) === tail) _fileOperations.delete(key);
  });
  return operation;
}
let _flushListenersInstalled = false;
let _leaving = false;

export async function flushPendingWrites() {
  // A provider may be created while another provider is finishing its read.
  let providers;
  do {
    providers = [..._liveInstances];
    const results = await Promise.allSettled(providers.map(inst => inst.flush()));
    const failures = results.filter(result => result.status === "rejected");
    if (failures.length) throw new AggregateError(failures.map(r => r.reason), "Could not save all Orbital files");
  } while ([..._liveInstances].some(inst => !providers.includes(inst) || inst.hasPendingWrites));
}

async function afterSaved(action) {
  if (_leaving) throw new Error("A save and navigation is already in progress");
  _leaving = true;
  // Commit any focused inline editor before taking the final snapshots.
  document.activeElement?.blur();
  const root = document.documentElement;
  const wasInert = root.inert;
  root.inert = true;
  try {
    await flushPendingWrites();
    await action();
  } finally {
    root.inert = wasInert;
    _leaving = false;
  }
}

export async function reloadApp(beforeReload) {
  let saved = false;
  try {
    await afterSaved(async () => {
      saved = true;
      await beforeReload?.();
      window.location.reload();
    });
    return true;
  } catch (err) {
    if (saved) throw err; // Configuration/navigation failures belong to the caller.
    console.error("[TauriAdapter] Reload cancelled: save failed", err);
    notify({ code: NOTICE.SAVE_FAILED, level: "error", message: t("sync.save.failedDesktopReload"), data: { before: "reload" } });
    return false;
  }
}

async function _installFlushListeners() {
  if (_flushListenersInstalled) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  _flushListenersInstalled = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPendingWrites().catch(err => console.error("[TauriAdapter] Background save failed", err));
    }
  });
  window.addEventListener("orbital:close-requested", async () => {
    if (_leaving) {
      await getInvoke()("close_after_flush", { saved: false });
      return;
    }
    try {
      await afterSaved(() => getInvoke()("close_after_flush", { saved: true }));
    } catch (err) {
      console.error("[TauriAdapter] Close cancelled: save failed", err);
      await getInvoke()("close_after_flush", { saved: false });
      notify({ code: NOTICE.SAVE_FAILED, level: "error", message: t("sync.save.failedDesktopClose"), data: { before: "close" } });
    }
  });
  window.addEventListener("keydown", event => {
    if (event.key === "F5" || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r")) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void reloadApp();
    }
  }, true);
  // The native shell must know the listener exists before any documents open.
  await getInvoke()("close_guard_ready");
}

// ============================================
// FilePersistence — Yjs persistence provider over Tauri commands
// ============================================
//
// Implements the subset of the IndexeddbPersistence interface that the
// rest of the codebase touches:
//
//   .synced       boolean       set true after first read
//   .whenSynced   Promise       resolves with `this` after first read
//   .on/off/once                minimal observable, fires "synced" once
//   .set(k, v)                  disk-health probe (writes a probe file)
//   .destroy()                  flushes pending writes, unsubscribes
//
// Writes are debounced (full state replaces the file each time, so the
// chain length stays bounded — no separate compaction step needed).
// All writes are atomic on the Rust side (write-to-temp + rename).

class FilePersistence {
  constructor(name, doc, { kind = "main", validateUpdate = null, legacyName = null } = {}) {
    this._legacyName = legacyName;
    this._validateUpdate = validateUpdate;
    this.name = name;
    this.doc = doc;
    this.kind = kind; // "main" -> doc_*, anything else -> cache_*
    this.synced = false;

    this._observers = new Map(); // event -> Set<fn>
    this._destroyed = false;
    this._writeTimer = null;
    // Promise chain that serializes writes so a slow Tauri call can't
    // overlap with a newer one and produce out-of-order persisted state.
    this._writeChain = Promise.resolve();
    this._revision = 1;
    this._savedRevision = 0;
    this._updateHandler = (_update, origin) => {
      if (this._destroyed || origin === this) return;
      this._revision++;
      if (this.synced) this._scheduleWrite();
    };
    // Capture edits made while the asynchronous file read is still running.
    this.doc.on("update", this._updateHandler);

    // Register before reading so a close also waits for initialization.
    _liveInstances.add(this);

    this.whenSynced = this._init();
    // If init fails, surface the error to anyone listening but don't
    // leave an unhandled rejection.
    this.whenSynced.catch((err) => this._emit("error", err));
  }

  get hasPendingWrites() {
    return !this._destroyed && this._savedRevision < this._revision;
  }

  async flush() {
    if (this._destroyed) return;
    // Never write a partial in-memory document over an unreadable file.
    try {
      await this.whenSynced;
    } catch {
      // A user can restore permissions after a failed initial read. Retry the
      // merge before allowing close/reload; never overwrite the unread file.
      if (!this._retryingRead) {
        this._retryingRead = this._init().finally(() => { this._retryingRead = null; });
        this.whenSynced = this._retryingRead;
      }
      await this.whenSynced;
    }
    do {
      clearTimeout(this._writeTimer);
      this._writeTimer = null;
      await this._writeChain;
      if (this.hasPendingWrites) await this._enqueueWrite();
    } while (this.hasPendingWrites);
  }

  // ---------- Init ----------

  async _init() {
    let bytes;
    try {
      bytes = await readVaultFile(this.kind, this.name);
    } catch (err) {
      throw new Error(`FilePersistence read failed (${this.name}): ${err}`);
    }

    if (bytes.length > 0) {
      this._validateUpdate?.(this.doc, bytes);
      Y.applyUpdate(this.doc, bytes, this);
    }
    // What the file holds, all of it now in the document (_performWrite).
    this._fileBytes = bytes;

    // Keep the old file untouched so an older process cannot overwrite the
    // upgraded cache. Re-read it at startup to recover late offline edits.
    if (this._legacyName) {
      const legacy = await readVaultFile("cache", this._legacyName);
      if (legacy.length) {
        this._validateUpdate?.(this.doc, legacy);
        Y.applyUpdate(this.doc, legacy, this);
      }
    }

    this.synced = true;
    if (this.hasPendingWrites) this._scheduleWrite();
    this._emit("synced");
    return this;
  }

  // ---------- Writes ----------

  _scheduleWrite() {
    if (this._writeTimer) clearTimeout(this._writeTimer);
    this._writeTimer = setTimeout(() => {
      this._writeTimer = null;
      this._enqueueWrite().catch(() => {});
    }, WRITE_DEBOUNCE_MS);
  }

  _enqueueWrite() {
    const next = this._writeChain.then(() => this._performWrite());
    // The sequencing tail recovers, while explicit flush callers receive errors.
    this._writeChain = next.catch(err => this._emit("error", err));
    return next;
  }

  async _performWrite() {
    return withFileLock(`${this.kind}:${this.name}`, async () => {
      if (this._destroyed || !this.hasPendingWrites) return;
      // Another provider may have saved after our initial read. Merge its Yjs
      // update (including deletion tombstones) before creating a new snapshot.
      // Read/decode failure must leave the existing file untouched. A file
      // still as this provider last read or wrote it is already in the
      // document: not merged again (0.14 s at every save of a 10 MB map).
      const bytes = await readVaultFile(this.kind, this.name);
      if (bytes.length && !sameBytes(bytes, this._fileBytes)) {
        this._validateUpdate?.(this.doc, bytes);
        Y.applyUpdate(this.doc, bytes, this);
      }
      const revision = this._revision;
      const fullState = Y.encodeStateAsUpdate(this.doc);
      await writeVaultFile(this.kind, this.name, fullState);
      this._fileBytes = fullState;
      this._savedRevision = revision;
    });
  }

  // ---------- Observable ----------

  on(event, fn) {
    if (!this._observers.has(event)) this._observers.set(event, new Set());
    this._observers.get(event).add(fn);
  }

  off(event, fn) {
    this._observers.get(event)?.delete(fn);
  }

  once(event, fn) {
    const wrapper = (...args) => {
      this.off(event, wrapper);
      fn(...args);
    };
    this.on(event, wrapper);
  }

  _emit(event, ...args) {
    const set = this._observers.get(event);
    if (!set) return;
    for (const fn of [...set]) {
      try {
        fn(...args);
      } catch (err) {
        console.error(`[FilePersistence:${this.name}] observer threw:`, err);
      }
    }
  }

  // ---------- Disk-health probe ----------

  /**
   * Maps the storage-health probe pattern (which uses the persistence
   * as a KV store on web) to a real disk write on Tauri. The probe
   * file lives in .orbital-cache/ and is overwritten on each call.
   * Errors from a full disk / readonly mount propagate up so the
   * existing storage-health monitor can mark storage broken.
   */
  async set(_key, _value) {
    // 3-byte payload "orb" — content doesn't matter, only that the
    // write succeeds. Constant so the file size on disk is stable.
    await writeVaultFile("cache", "probe", new Uint8Array([0x6f, 0x72, 0x62]));
  }

  // ---------- Teardown ----------

  async destroy() {
    if (this._destroyed) return;
    await this.flush();
    clearTimeout(this._writeTimer);
    this.doc.off("update", this._updateHandler);
    this._destroyed = true;
    this._observers.clear();
    _liveInstances.delete(this);
  }

}

// ============================================
// StorageAdapter surface
// ============================================

export function createDocPersistence(docId, doc, options = {}) {
  // `kind` lets the adapter route temp/linked/share docs into the
  // .orbital-cache/ subfolder so they don't appear in the user's
  // vault listing alongside their real documents.
  //
  // - "main"   -> <vault>/<docId>.orbital  (user-visible)
  // - "linked" -> <vault>/.orbital-cache/<docId>.orbital
  // - "share"  -> <vault>/.orbital-cache/<docId>.orbital
  // - "temp"   -> <vault>/.orbital-cache/<docId>.orbital
  const kind = options.kind === "main" ? "main" : "cache";
  // For non-main docs we keep the docId as the filename verbatim so
  // recovery flows can still locate the cache by id.
  return new FilePersistence(docId, doc, { kind });
}

export function createUserPersistence(userId, doc) {
  // The user CRDT (vault index) is metadata, not a user-visible
  // document — store it in the cache folder under a stable name.
  return new FilePersistence(`user_v2_${userId}`, doc, {
    kind: "cache", validateUpdate: assertCompatibleVaultStructure, legacyName: `user_${userId}`,
  });
}

export async function deleteDocCache(docId) {
  // A still-live provider could recreate the file after deletion. Callers must
  // complete teardown first; a failed final save keeps the provider registered.
  if ([..._liveInstances].some(inst => inst.name === docId)) {
    throw new Error("Cannot delete a document while its storage is still open");
  }
  for (const kind of ["main", "cache"]) {
    await withFileLock(`${kind}:${docId}`, async () => {
      if ([..._liveInstances].some(inst => inst.name === docId)) {
        throw new Error("Document was reopened before cache deletion");
      }
      await getInvoke()(kind === "main" ? "doc_delete" : "cache_delete",
        kind === "main" ? { docId } : { name: docId });
    });
  }
}

/**
 * Add a map's content to its file in the vault folder (moving an online map
 * to this device): the file's content and `state`, merged, replace the file.
 * Nothing already in the file is lost, and a provider saving the open map
 * merges the file again before each save.
 */
export async function mergeIntoMapFile(docId, state) {
  await withFileLock(`main:${docId}`, async () => {
    const bytes = await readVaultFile("main", docId);
    const merged = bytes.length ? Y.mergeUpdates([bytes, state]) : state;
    await writeVaultFile("main", docId, merged);
  });
}

/** A map's file in the vault folder, or null when it has none. */
export async function readMapFile(docId) {
  return withFileLock(`main:${docId}`, async () => {
    const bytes = await readVaultFile("main", docId);
    return bytes.length ? bytes : null;
  });
}

// ---------- Attachment files (tauri/src/attachment_files.rs) ----------
// Scopes: "device" (maps stored on this device) and "offline" (copies of
// online maps' files; account data). Bytes cross the bridge raw.

export async function putStoredAttachment(scope, docId, blobId, bytes) {
  await getInvoke()("blob_write", bytes, {
    headers: { "x-scope": scope, "x-doc-id": docId, "x-blob-id": blobId },
  });
}

export async function getStoredAttachment(scope, docId, blobId) {
  try {
    const bytes = await getInvoke()("blob_read", { scope, docId, blobId });
    return bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : new Uint8Array(bytes || []);
  } catch (err) {
    if (String(err) === "not_found") return null;
    throw err;
  }
}

export async function deleteStoredAttachment(scope, docId, blobId) {
  await getInvoke()("blob_delete", { scope, docId, blobId });
}

export async function listStoredAttachments(scope, docId) {
  return (await getInvoke()("blob_list", { scope, docId })).map((entry) => {
    let header = null;
    try { header = entry.header ? JSON.parse(entry.header) : null; } catch { /* unreadable header: listed without it */ }
    return { id: entry.id, size: entry.size, usedAt: entry.stored_at || 0, header };
  });
}

/** The maps with at least one file in this scope (a folder left empty holds nothing). */
export async function listStoredAttachmentMaps(scope) {
  return getInvoke()("blob_maps", { scope });
}

export async function deleteStoredMapAttachments(scope, docId) {
  return getInvoke()("blob_delete_map", { scope, docId });
}

export async function clearAccountCaches({ userId, docIds = [] } = {}) {
  if (!userId || userId.startsWith('local-')) return { docs: 0, prefs: 0, enumerated: true };
  const names = [...new Set([`user_v2_${userId}`, ...docIds])];
  if ([..._liveInstances].some(instance => instance.kind !== 'main' && names.includes(instance.name))) {
    throw new Error('Cannot clear account caches while their storage is still open');
  }
  for (const name of names) await getInvoke()('cache_delete', { name });
  // Offline copies of the online maps' attachments are account data too.
  for (const docId of docIds) await getInvoke()('blob_delete_map', { scope: 'offline', docId });
  // So are the online maps' files in the vault folder (every opened map has
  // one). The caller passes only maps the server has entirely (checked
  // before signing out); a map still open keeps its file.
  let files = 0;
  for (const docId of docIds) {
    if ([..._liveInstances].some(instance => instance.name === docId)) continue;
    await withFileLock(`main:${docId}`, () => getInvoke()('doc_delete', { docId }));
    files++;
  }
  // Maps on this device (their files and attachments) and device
  // preferences remain on disk.
  return { docs: names.length, files, prefs: 0, enumerated: true };
}

export async function clearAllCaches() {
  const invoke = getInvoke();
  let docs = 0;
  let prefs = 0;

  if ([..._liveInstances].some(inst => inst.kind !== "main" && !inst.name.startsWith("user_v2_local-"))) {
    throw new Error("Cannot clear caches while online storage is still open");
  }
  // Only clear the cache folder — user-visible .orbital files are
  // user data, not caches, and must survive a "logout cleanup."
  docs = await invoke("cache_clear");

  // Keep the stable local identity/key that owns user_v2_local-*.orbital.
  // Clearing it would orphan local maps even though their files survived.
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("orbital_local_")) continue;
      if (["orbital-", "orbital_", "project-planner-"].some(prefix => key.startsWith(prefix))) {
        localStorage.removeItem(key);
        prefs++;
      }
    }
  } catch (err) {
    console.warn("[TauriAdapter] pref cleanup failed:", err);
  }

  return { docs, prefs, enumerated: true };

}

/**
 * Phase 5a: load the persisted Tauri config so getSyncUrl /
 * isSyncAvailable can return correct values. Idempotent.
 *
 * Call this once at startup from main.js before the bootstrap
 * routes into local-only vs online. Failure is treated as "no URL
 * configured" — local-only mode — rather than blocking startup.
 */
export async function loadAdapterConfig() {
  if (_configLoaded) return;
  try {
    await _installFlushListeners();
  } catch (err) {
    // No persistence has opened yet. Stop bootstrap and give the user a way
    // to retry after closing the other process or repairing the configuration.
    const panel = document.createElement("main");
    panel.style.cssText = "padding:2rem;max-width:48rem;margin:auto";
    const heading = document.createElement("h1");
    heading.textContent = t("sync.vault.openFailed");
    const message = document.createElement("p");
    message.textContent = String(err);
    const retry = document.createElement("button");
    retry.textContent = t("common.retry");
    retry.addEventListener("click", () => window.location.reload());
    panel.append(heading, message, retry);
    document.body.replaceChildren(panel);
    throw err;
  }
  _configLoaded = true;
  try {
    const url = await getInvoke()("config_get_server_url");
    _serverUrl = typeof url === "string" && url.length > 0 ? url : null;
    if (_serverUrl) {
      console.log("[TauriAdapter] Online mode: server =", _serverUrl);
    } else {
      console.log("[TauriAdapter] Local-only mode (no server configured)");
    }
  } catch (err) {
    console.warn("[TauriAdapter] config load failed, defaulting to local-only:", err);
    _serverUrl = null;
  }
}

export function getSyncUrl(path) {
  if (!_serverUrl) return null;
  // Convert https:// → wss:// (or http → ws) and append the path.
  // Trailing-slash safety: strip trailing / from origin if present.
  const origin = _serverUrl.replace(/\/+$/, "");
  const wsOrigin = origin.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
  return `${wsOrigin}${path}`;
}

export function isSyncAvailable() {
  return _serverUrl !== null;
}

/**
 * The configured HTTP origin (not WebSocket) for fetch() calls.
 * Returns null when local-only. Used by code paths that need to
 * route HTTP API calls to a Tauri-configured server (Phase 5b/c).
 */
export function getServerOrigin() {
  if (!_serverUrl) return null;
  return _serverUrl.replace(/\/+$/, "");
}

export async function setServerUrl(url) {
  const invoke = getInvoke();
  await invoke("config_set_server_url", { serverUrl: url || null });
  // Don't update _serverUrl in memory — the frontend's auth and sync
  // state were initialized for the previous mode. Caller must reload
  // the window for the change to take effect, just like the vault
  // path change.
}

// ============================================
// Global fetch routing shim (Phase 5b)
// ============================================
//
// In Tauri online mode the page is served from the `tauri://localhost`
// asset protocol, so any code path that fetches a relative URL like
// `/api/notifications` resolves to the Tauri page origin and fails.
//
// Rather than touch every call site (~40 across the codebase) we
// install a one-time wrapper around `window.fetch` that prefixes
// API-prefix URLs with the configured server origin. Already-absolute
// URLs are passed through unchanged, so the existing manual prefix in
// `engine/auth/handlers.js api()` doesn't double up.
//
// The proxy is a no-op when the server URL isn't set (local-only
// mode) — `installFetchProxy()` returns early.

const API_PREFIXES = [
  "/api/",
  "/auth/",
  "/e2ee/",
  "/health",
  "/users/",
  "/keypair",
  "/shares/",
  "/invitations/",
  "/records/",
];

let _fetchProxyInstalled = false;

export function installFetchProxy() {
  if (_fetchProxyInstalled) return;
  if (!_serverUrl) return; // local-only mode, no proxy needed
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  const origin = _serverUrl.replace(/\/+$/, "");
  const original = window.fetch.bind(window);

  function shouldPrefix(url) {
    if (typeof url !== "string") return false;
    if (!url.startsWith("/")) return false;
    return API_PREFIXES.some((p) => url.startsWith(p));
  }

  // Helper: pull the path part out of an absolute URL string and
  // check if it matches our API allowlist relative to the Tauri page
  // origin. Returns the path to re-route, or null if no rewrite is needed.
  const tauriOrigin = window.location.origin;
  function rewritablePathFromAbsoluteUrl(u) {
    if (typeof u !== "string") return null;
    if (!u.startsWith(tauriOrigin)) return null;
    const path = u.slice(tauriOrigin.length);
    return shouldPrefix(path) ? path : null;
  }

  window.fetch = function fetchProxy(input, init) {
    try {
      if (typeof input === "string" && shouldPrefix(input)) {
        return original(origin + input, init);
      }
      if (typeof URL !== "undefined" && input instanceof URL) {
        // URL.href is always absolute. If it was constructed from a
        // relative API path, the Tauri page origin is now baked in —
        // strip it and re-route. Otherwise pass through unchanged.
        const path = rewritablePathFromAbsoluteUrl(input.href);
        if (path) {
          return original(origin + path, init);
        }
        return original(input, init);
      }
      if (typeof Request !== "undefined" && input instanceof Request) {
        // Request objects expose an absolute .url even when constructed
        // from a relative string — the page origin (tauri://...) is
        // already baked in. Strip that and re-route to the server origin
        // when the path matches.
        const path = rewritablePathFromAbsoluteUrl(input.url);
        if (path) {
          // Reconstruct the Request with a new URL but preserve init.
          return original(new Request(origin + path, input), init);
        }
      }
    } catch (err) {
      console.warn("[TauriAdapter] fetch proxy error, falling through:", err);
    }
    return original(input, init);
  };

  _fetchProxyInstalled = true;
  console.log("[TauriAdapter] Fetch proxy installed →", origin);
}

/**
 * Phase 4: surface the active vault path so the UI can show users
 * where their files live. Returns the absolute path as a string.
 * This is the only adapter method that's Tauri-specific in its
 * surface (the web adapter has no equivalent — there is no "path"
 * concept for IndexedDB) so consumers should feature-detect via
 * `typeof getVaultPath === 'function'` before calling.
 */
export async function getVaultPath() {
  const invoke = getInvoke();
  return invoke("vault_get_path");
}

/**
 * Phase 4: change the active vault directory. The new path is
 * persisted to the app config file so it survives restart.
 * Note: existing in-memory CRDTs aren't moved — the change takes
 * effect after a successful flush and page reload. Existing files are not
 * moved; the old vault stays available at its original path.
 */
export async function setVaultPath(path) {
  const invoke = getInvoke();
  return invoke("vault_set_path", { path });
}

/** Local map payload size; does not modify the file or enter cloud quota. */
export async function readDocSize(docId) {
  const bytes = await readVaultFile('main', docId);
  return bytes.length ? bytes.length : null;
}
